/**
 * @module projectImages
 *
 * Service layer for project image management.
 * Handles upload/delete/fetch/reorder of project images
 * using Supabase Storage + claims_project_images metadata table.
 */
import { supabase, requireOrganizationId } from "./supabase";

const BUCKET_NAME = "project-images";

export interface ProjectImage {
  id: string;
  project_id: string;
  organization_id: string;
  storage_path: string;
  original_filename: string | null;
  display_order: number;
  is_cover: boolean;
  uploaded_by: string | null;
  created_at: string;
  /** Computed public URL */
  url: string;
}

/**
 * Generate a public URL for a storage path.
 */
export function getProjectImageUrl(storagePath: string): string {
  const { data } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(storagePath);
  return data.publicUrl;
}

/**
 * Fetch all images for a project, ordered by display_order.
 */
export async function fetchProjectImages(
  projectId: string
): Promise<ProjectImage[]> {
  const { data, error } = await supabase
    .from("claims_project_images" as never)
    .select("*")
    .eq("project_id", projectId)
    .order("display_order", { ascending: true })
    .order("created_at", { ascending: true });

  if (error) throw error;

  return ((data as unknown as Omit<ProjectImage, "url">[]) || []).map(
    (img) => ({
      ...img,
      url: getProjectImageUrl(img.storage_path),
    })
  );
}

/**
 * Fetch the cover image (or first image) for a project.
 * Used in project grid cards.
 */
export async function fetchCoverImage(
  projectId: string
): Promise<ProjectImage | null> {
  // Try cover first
  const { data: coverData } = await supabase
    .from("claims_project_images" as never)
    .select("*")
    .eq("project_id", projectId)
    .eq("is_cover", true)
    .limit(1)
    .single();

  if (coverData) {
    const img = coverData as unknown as Omit<ProjectImage, "url">;
    return { ...img, url: getProjectImageUrl(img.storage_path) };
  }

  // Fallback: first image by display_order
  const { data: firstData } = await supabase
    .from("claims_project_images" as never)
    .select("*")
    .eq("project_id", projectId)
    .order("display_order", { ascending: true })
    .limit(1)
    .single();

  if (firstData) {
    const img = firstData as unknown as Omit<ProjectImage, "url">;
    return { ...img, url: getProjectImageUrl(img.storage_path) };
  }

  return null;
}

/**
 * Fetch cover images for multiple projects in batch.
 * Returns a Map of projectId → coverImageUrl.
 */
export async function fetchCoverImagesForProjects(
  projectIds: string[]
): Promise<Map<string, { url: string; totalCount: number }>> {
  if (projectIds.length === 0) return new Map();

  // Get all images for these projects in one query
  const { data, error } = await supabase
    .from("claims_project_images" as never)
    .select("project_id, storage_path, is_cover, display_order")
    .in("project_id", projectIds)
    .order("display_order", { ascending: true });

  if (error) throw error;

  const result = new Map<string, { url: string; totalCount: number }>();
  const projectImageCounts = new Map<string, number>();
  const projectCoverPaths = new Map<string, string>();

  type ImageRow = {
    project_id: string;
    storage_path: string;
    is_cover: boolean;
    display_order: number;
  };

  for (const row of (data as unknown as ImageRow[]) || []) {
    // Count
    projectImageCounts.set(
      row.project_id,
      (projectImageCounts.get(row.project_id) || 0) + 1
    );

    // Prefer cover image, otherwise first image
    if (row.is_cover) {
      projectCoverPaths.set(row.project_id, row.storage_path);
    } else if (!projectCoverPaths.has(row.project_id)) {
      projectCoverPaths.set(row.project_id, row.storage_path);
    }
  }

  for (const [pid, path] of projectCoverPaths) {
    result.set(pid, {
      url: getProjectImageUrl(path),
      totalCount: projectImageCounts.get(pid) || 0,
    });
  }

  return result;
}

/**
 * Fetch all image URLs for a project (used for slideshow on cards).
 */
export async function fetchAllProjectImageUrls(
  projectId: string
): Promise<string[]> {
  const { data, error } = await supabase
    .from("claims_project_images" as never)
    .select("storage_path")
    .eq("project_id", projectId)
    .order("display_order", { ascending: true });

  if (error) throw error;

  return (
    (data as unknown as { storage_path: string }[]) || []
  ).map((row) => getProjectImageUrl(row.storage_path));
}

/**
 * Upload an image file to storage and create a metadata record.
 */
export async function uploadProjectImage(
  projectId: string,
  file: File,
  options?: { isCover?: boolean; displayOrder?: number }
): Promise<ProjectImage> {
  const orgId = requireOrganizationId();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  // Generate unique storage path: orgId/projectId/timestamp_filename
  const timestamp = Date.now();
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `${orgId}/${projectId}/${timestamp}_${sanitizedName}`;

  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(storagePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (uploadError) throw uploadError;

  // If this is a cover image, unset other covers first
  if (options?.isCover) {
    await supabase
      .from("claims_project_images" as never)
      .update({ is_cover: false } as never)
      .eq("project_id", projectId);
  }

  // Auto-detect if this is the very first image, make it the cover automatically
  let shouldBeCover = options?.isCover ?? false;
  if (!shouldBeCover) {
    const { count } = await supabase
      .from("claims_project_images" as never)
      .select("id", { count: "exact", head: true })
      .eq("project_id", projectId);
    if (count === 0) {
      shouldBeCover = true;
    }
  }

  // Get current max display order
  let displayOrder = options?.displayOrder ?? 0;
  if (!options?.displayOrder) {
    const { data: maxOrderData } = await supabase
      .from("claims_project_images" as never)
      .select("display_order")
      .eq("project_id", projectId)
      .order("display_order", { ascending: false })
      .limit(1)
      .single();

    displayOrder = maxOrderData
      ? (maxOrderData as unknown as { display_order: number }).display_order + 1
      : 0;
  }

  // Insert metadata record
  const { data: imageData, error: insertError } = await supabase
    .from("claims_project_images" as never)
    .insert({
      project_id: projectId,
      organization_id: orgId,
      storage_path: storagePath,
      original_filename: file.name,
      display_order: displayOrder,
      is_cover: shouldBeCover,
      uploaded_by: user.id,
    } as never)
    .select()
    .single();

  if (insertError) {
    // Cleanup: remove uploaded file if metadata insert fails
    await supabase.storage.from(BUCKET_NAME).remove([storagePath]);
    throw insertError;
  }

  const img = imageData as unknown as Omit<ProjectImage, "url">;
  return { ...img, url: getProjectImageUrl(img.storage_path) };
}

/**
 * Delete a project image (storage + metadata).
 */
export async function deleteProjectImage(imageId: string): Promise<void> {
  // Fetch the image to get storage path
  const { data: imageData, error: fetchError } = await supabase
    .from("claims_project_images" as never)
    .select("storage_path")
    .eq("id", imageId)
    .single();

  if (fetchError) throw fetchError;
  if (!imageData) throw new Error("Image not found");

  const storagePath = (imageData as unknown as { storage_path: string })
    .storage_path;

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([storagePath]);

  if (storageError) throw storageError;

  // Delete metadata
  const { error: deleteError } = await supabase
    .from("claims_project_images" as never)
    .delete()
    .eq("id", imageId);

  if (deleteError) throw deleteError;
}

/**
 * Reorder project images.
 */
export async function reorderProjectImages(
  imageIds: string[]
): Promise<void> {
  const updates = imageIds.map((id, index) =>
    supabase
      .from("claims_project_images" as never)
      .update({ display_order: index } as never)
      .eq("id", id)
  );

  await Promise.all(updates);
}

/**
 * Set an image as the cover image for a project.
 */
export async function setCoverImage(
  imageId: string,
  projectId: string
): Promise<void> {
  // Unset all covers for this project
  await supabase
    .from("claims_project_images" as never)
    .update({ is_cover: false } as never)
    .eq("project_id", projectId);

  // Set the new cover
  const { error } = await supabase
    .from("claims_project_images" as never)
    .update({ is_cover: true } as never)
    .eq("id", imageId);

  if (error) throw error;
}
